/* eslint-disable max-lines -- error/empty-state branching for three independent queries (list,
   stats, filter options) is inherent to this route; splitting it up would scatter closely-related
   branches across files rather than remove any actual bloat. */
import {
  DimeCard,
  DimeCardAction,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeDataTable,
} from '@nudool/design-system';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ReceiptText, ShieldAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ListEmptyState } from '../../components/ListEmptyState.js';
import { ListErrorState } from '../../components/ListErrorState.js';
import {
  parseListRouteSearch,
  type ListRouteSearch,
} from '../../hooks/use-list-table-helpers/index.js';
import { makeSalesOrderFilterHandlers } from '../../modules/sales/make-sales-order-filter-handlers.js';
import { makeSalesOrderColumns } from '../../modules/sales/sales-order-columns.js';
import { SalesOrderCreateAction } from '../../modules/sales/sales-order-create-action.js';
import { SalesOrderFilterChips } from '../../modules/sales/sales-order-filter-chips.js';
import {
  buildFiltersErrorMessage,
  buildSalesOrderEmptyMessage,
  handleSalesOrderRowClick,
} from '../../modules/sales/sales-order-list-utils.js';
import { SalesOrderMetricsCards } from '../../modules/sales/sales-order-metrics-cards.js';
import { SalesOrderToolbarFilters } from '../../modules/sales/sales-order-toolbar-filters.js';
import { useSalesOrderFilterOptions } from '../../modules/sales/use-sales-order-filter-options.js';
import { useSalesOrderStatusShortcuts } from '../../modules/sales/use-sales-order-status-shortcuts.js';
import { useSalesOrdersListQuery } from '../../modules/sales/use-sales-orders-list-query.js';

export const Route = createFileRoute('/sales-orders/')({
  validateSearch: (search: Record<string, unknown>): ListRouteSearch =>
    parseListRouteSearch(search),
  component: SalesOrdersIndexPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function SalesOrdersIndexPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<number[]>([]);
  const [brandFilter, setBrandFilter] = useState<string[]>([]);
  const [stateFilter, setStateFilter] = useState<string[]>([]);
  const [pendingPaymentOnly, setPendingPaymentOnly] = useState(false);

  const {
    statuses,
    brands,
    states,
    statesLoading,
    error: filtersError,
    statusesError,
    brandsError,
    statesError,
  } = useSalesOrderFilterOptions();
  const {
    salesOrders,
    loading,
    error,
    refetch,
    tableProps,
    search,
    stats,
    statsLoading,
    statsError,
    statsRefetch,
  } = useSalesOrdersListQuery({
    statusFilter,
    brandFilter,
    stateFilter,
    pendingPaymentOnly,
  });

  // Brand is only informative once a user can actually see more than one -- with a single
  // accessible brand every row would repeat the same value.
  const showBrandColumn = brands.length > 1;
  // Same rule as the State filter itself: a single-state user has nothing to compare, so the
  // column is noise rather than a choice.
  const showStateColumn = states.length > 1;
  const columns = useMemo(
    () => makeSalesOrderColumns({ showBrandColumn, showStateColumn }),
    [showBrandColumn, showStateColumn],
  );
  // useListTable resets the page when `where` changes; this reset is kept for signature parity.
  const { isDraftActive, isPendingPaymentActive, onFilterDraft, onFilterPendingPayment } =
    useSalesOrderStatusShortcuts({
      statusFilter,
      setStatusFilter,
      pendingPaymentOnly,
      setPendingPaymentOnly,
      reset: tableProps.onFirstPage,
    });
  const hasActiveFilters =
    statusFilter.length > 0 || brandFilter.length > 0 || stateFilter.length > 0;
  const {
    onBrandFilterChange,
    onStateFilterChange,
    onStatusFilterChange,
    onRemoveBrand,
    onRemoveState,
    onRemoveStatus,
    onClearAll,
  } = makeSalesOrderFilterHandlers({
    setBrandFilter,
    setStateFilter,
    setStatusFilter,
    setPendingPaymentOnly,
    reset: tableProps.onFirstPage,
  });

  const listFilters = (
    <SalesOrderToolbarFilters
      brands={brands}
      brandFilter={brandFilter}
      onBrandFilterChange={onBrandFilterChange}
      states={states}
      stateFilter={stateFilter}
      onStateFilterChange={onStateFilterChange}
      statuses={statuses}
      statusFilter={statusFilter}
      onStatusFilterChange={onStatusFilterChange}
    />
  );

  const listFilterChips = hasActiveFilters ? (
    <SalesOrderFilterChips
      brands={brands}
      brandFilter={brandFilter}
      onRemoveBrand={onRemoveBrand}
      states={states}
      stateFilter={stateFilter}
      onRemoveState={onRemoveState}
      statuses={statuses}
      statusFilter={statusFilter}
      onRemoveStatus={onRemoveStatus}
      pendingPaymentOnly={pendingPaymentOnly}
      onClearAll={onClearAll}
    />
  ) : undefined;

  // Ship-to-state scoping (getAllowedStateIds) makes every sales order query return zero rows for
  // a caller with no iam.state_user grants -- distinct from a genuinely empty or over-filtered
  // list, so it needs its own empty state rather than reading as "no orders yet".
  const noStatesAssigned = !statesLoading && !statesError && states.length === 0;
  const showNoStatesEmptyState = !loading && !error && noStatesAssigned && salesOrders.length === 0;
  // Don't commit to the generic empty state while states is still in flight, or a caller with no
  // state grants briefly sees "No sales orders yet" before the no-states-assigned state replaces it.
  const showEmptyState =
    !showNoStatesEmptyState &&
    !statesLoading &&
    !loading &&
    !error &&
    salesOrders.length === 0 &&
    !search &&
    !hasActiveFilters;
  const noResultsMessage = buildSalesOrderEmptyMessage(search, hasActiveFilters);

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base flex items-center gap-2">Sales Orders</DimeCardTitle>
        <DimeCardDescription className="text-sm">
          Review and manage open invoices across your entire brand portfolio.
        </DimeCardDescription>
        <DimeCardAction>
          <SalesOrderCreateAction />
        </DimeCardAction>
      </DimeCardHeader>

      {filtersError && (
        <ListErrorState
          className="mb-4"
          title="Some filters failed to load"
          message={buildFiltersErrorMessage({
            statuses: Boolean(statusesError),
            brands: Boolean(brandsError),
            states: Boolean(statesError),
          })}
        />
      )}

      {error && salesOrders.length === 0 ? (
        <ListErrorState
          title="Could not load sales orders"
          message={error.message}
          onRetry={() => void refetch()}
        />
      ) : (
        <>
          {statsError ? (
            <ListErrorState
              className="mb-4"
              title="Could not load order metrics"
              message={statsError.message}
              onRetry={() => void statsRefetch()}
            />
          ) : (
            !showEmptyState &&
            !showNoStatesEmptyState && (
              <SalesOrderMetricsCards
                stats={stats}
                loading={statsLoading}
                isDraftActive={isDraftActive}
                isPendingPaymentActive={isPendingPaymentActive}
                onFilterDraft={onFilterDraft}
                onFilterPendingPayment={onFilterPendingPayment}
              />
            )
          )}
          {showNoStatesEmptyState ? (
            <ListEmptyState
              icon={<ShieldAlert />}
              title="No states assigned"
              description="Ask an administrator to grant you state access to view sales orders."
            />
          ) : showEmptyState ? (
            <ListEmptyState
              icon={<ReceiptText />}
              title="No sales orders yet"
              description="Create a sales order to start tracking customer fulfillment."
              action={<SalesOrderCreateAction />}
            />
          ) : (
            <DimeDataTable
              columns={columns}
              data={salesOrders}
              maxBodyHeight="viewport"
              searchColumn="num"
              searchPlaceholder="Search by order # or customer…"
              filters={listFilters}
              filterChips={listFilterChips}
              showViewOptions
              emptyMessage={noResultsMessage}
              onRowClick={(salesOrder) => handleSalesOrderRowClick(salesOrder, navigate)}
              loadingRowCount={tableProps.pageSize}
              exportFileName="sales-orders"
              {...tableProps}
              // Gated on statesLoading so "no matches" can't flash before state access is known.
              isLoading={tableProps.isLoading || statesLoading}
            />
          )}
        </>
      )}
    </DimeCard>
  );
}
